import * as React from 'react'

import {
  Box,
  Button,
  Chevron,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import i18n from 'modules/core/lib/i18n'
import type { ModalStates } from 'modules/core/lib/questionModalStates'

import NewTemplateQuestion from 'modules/surveys/components/Questions/AddQuestion/NewQuestionConfigModal'
import EditQuestionsTemplateCategory from 'modules/surveys/components/Questions/Categories/EditCategoryModal'
import DeleteQuestionsTemplateCategory from 'modules/surveys/components/Questions/Categories/RemoveCategoryModal'
import type { CreateTemplateQuestion } from 'modules/surveys/pages/QuestionsTemplates/MainContent/QuestionsContent/List/types'
import type QuestionsTemplateCategory from 'modules/surveys/store/models/QuestionsTemplateCategory'

interface Props {
  questionsTemplateCategory: QuestionsTemplateCategory
  createTemplateQuestion: (
    data: CreateTemplateQuestion,
    questionsTemplateCategoryId?: string
  ) => Promise<unknown>
  updateQuestionsTemplateCategory: (data: Record<string, unknown>) => Promise<unknown>
  deleteQuestionsTemplateCategory: (data: { id: string }) => Promise<unknown>
  expanded: boolean
  isCore: boolean
}

function CategoryHeader({
  questionsTemplateCategory,
  createTemplateQuestion,
  updateQuestionsTemplateCategory,
  deleteQuestionsTemplateCategory,
  expanded,
  isCore,
}: Props) {
  const [isOpen, toggleOpen] = useToggle(false)
  const [hover, toggleHover] = useToggle(false)
  const [categoryModal, setCategoryModal] = React.useState<ModalStates>('none')
  const [questionModal, setQuestionModal] = React.useState<ModalStates>('none')

  const createTemplateQuestionWithCategory = async (data: Record<string, unknown>) => {
    createTemplateQuestion(data, questionsTemplateCategory.attributes.id)
  }

  const editQuestionsTemplateCategory = async (data: Record<string, unknown>) =>
    updateQuestionsTemplateCategory({
      id: questionsTemplateCategory.attributes.id,
      name: data.name,
    })

  const editAction = {
    onClick: () => setCategoryModal('edit'),
    title: i18n.t('surveys.form.question_categories.edit_button'),
  }

  const deleteAction = {
    onClick: () => setCategoryModal('delete'),
    type: 'wrong',
    title: i18n.t('surveys.form.question_categories.delete_button'),
  }

  const actions = [editAction, deleteAction]

  return (
    <>
      <Box
        flexDirection="row"
        paddingLeft="s32"
        paddingX="s16"
        borderRadius={{
          all: 'abs004',
        }}
        border={{
          all: {
            style: 'solid',
            width: 's1',
            color: 'grey400',
          },
        }}
        flexGrow
        cursor="pointer"
        background="grey200"
        expanded={expanded}
      >
        <Box justifyContent="center" paddingX="s2">
          <Chevron direction={expanded ? 'down' : 'right'} type="secondary" />
        </Box>
        <Box paddingY="s8" paddingX="s12" gap="s2" flexGrow>
          <Text.Normal color="grey1000" weight="medium" size="300">
            {questionsTemplateCategory.attributes.name}
          </Text.Normal>
          <Text.Normal color="grey800" weight="medium" size="200">
            {i18n.tp('surveys.form.question_categories.question_configs_count', {
              count: questionsTemplateCategory.templateQuestions.length,
            })}
          </Text.Normal>
        </Box>
        {!isCore && (
          <Box
            alignItems="center"
            flexDirection="row"
            gap="s8"
            onClick={(ev) => ev.stopPropagation()}
          >
            <Tooltip.Alt
              attachment="top center"
              offset="-10px 0"
              open={hover}
              toggle={toggleHover}
              label={i18n.t('surveys.form.question_categories.add_question')}
            >
              <Button type="secondary" onClick={() => setQuestionModal('new')} iconName="add" />
            </Tooltip.Alt>
            <Popover attachment="bottom center" toggle={toggleOpen} open={isOpen}>
              <Button type="secondary" onClick={toggleOpen(true)} iconName="ellipsis" />
              <PopoverContent onClose={toggleOpen(false)} options={actions} />
            </Popover>
          </Box>
        )}
      </Box>
      {questionModal === 'new' && (
        <NewTemplateQuestion
          onClose={() => setQuestionModal('none')}
          onSubmit={createTemplateQuestionWithCategory}
        />
      )}

      {categoryModal === 'edit' && (
        <EditQuestionsTemplateCategory
          onClose={() => setCategoryModal('none')}
          onSubmit={editQuestionsTemplateCategory}
          category={questionsTemplateCategory}
        />
      )}

      {categoryModal === 'delete' && (
        <DeleteQuestionsTemplateCategory
          onClose={() => setCategoryModal('none')}
          onSubmit={deleteQuestionsTemplateCategory}
          category={questionsTemplateCategory}
          questionsCount={questionsTemplateCategory.templateQuestions.length}
        />
      )}
    </>
  )
}

export default observer(CategoryHeader)
